import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { PasswordInput } from './ui/password-input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { toast } from '../hooks/use-toast';
import api from '../services/api';

// Page publique : l'utilisateur arrive ici depuis le lien recu par e-mail.
// Elle n'exige aucune connexion, le jeton present dans l'URL fait foi.
// Ajout au parcours existant : la gestion des mots de passe par
// l'administrateur reste inchangee.
const MIN_LEN = 8;

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const tropCourt = password.length > 0 && password.length < MIN_LEN;
  const discordant = confirm.length > 0 && password !== confirm;
  const peutValider = password.length >= MIN_LEN && password === confirm && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!peutValider) return;
    setLoading(true);
    try {
      await api.resetPassword(token, password);
      setDone(true);
      toast({ title: 'Mot de passe enregistré', description: 'Vous pouvez maintenant vous connecter.' });
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      toast({
        title: 'Lien invalide',
        description: err?.message || "Ce lien est expiré ou a déjà été utilisé. Refaites une demande depuis la page de connexion.",
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 p-4">
        <Card className="w-full max-w-md bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Lien incomplet</CardTitle>
            <CardDescription className="text-slate-400">
              Ce lien ne contient pas de jeton. Ouvrez-le directement depuis l'e-mail reçu,
              sans le modifier.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => navigate('/login')}>
              Retour à la connexion
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 p-4">
      <Card className="w-full max-w-md bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Choisir votre mot de passe</CardTitle>
          <CardDescription className="text-slate-400">
            {done
              ? 'Votre mot de passe est enregistré. Redirection vers la connexion...'
              : `Choisissez un mot de passe d'au moins ${MIN_LEN} caractères. Il remplacera celui qui vous avait été communiqué.`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!done && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="new-password" className="text-slate-300">Nouveau mot de passe</Label>
                <PasswordInput
                  id="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 mt-1"
                />
                {tropCourt && (
                  <p className="text-amber-400 text-xs mt-1">
                    Encore {MIN_LEN - password.length} caractère(s).
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="confirm-password" className="text-slate-300">Confirmer</Label>
                <PasswordInput
                  id="confirm-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 mt-1"
                />
                {discordant && (
                  <p className="text-red-400 text-xs mt-1">
                    Les deux saisies ne correspondent pas.
                  </p>
                )}
              </div>
              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                disabled={!peutValider}
              >
                {loading ? 'Enregistrement...' : 'Enregistrer mon mot de passe'}
              </Button>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full text-slate-400 hover:text-slate-200 text-sm"
              >
                Retour à la connexion
              </button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
